import React from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import { useSessions } from "@/hooks/ai-mode/useSessions";
import { hasMaaranToken } from "@/utils/easygtm";
import {
  ChevronDown,
  Loader,
  Loader2,
  MessageSquare,
  Play,
  SquarePen,
} from "lucide-react";
import { useRouter } from "next/router";
import SessionItem from "./sessionItem";
import {
  endOfWeek,
  isThisWeek,
  isToday,
  isValid,
  isWithinInterval,
  startOfWeek,
  subWeeks,
} from "date-fns";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setPreviewOpen } from "@/store/assistance";
import useGenerateMaaranToken from "@/hooks/ai-mode/useGenerateMaaranToken";
import { useSelector } from "react-redux";
import { useAssistance } from "@/context/assistanceProvider";

const prepareData = (sessions) => {
  if (!Array.isArray(sessions) || sessions.length === 0) return {};

  const now = new Date();
  const options = { weekStartsOn: 1 };

  const startOfLastWeek = startOfWeek(subWeeks(now, 1), options);
  const endOfLastWeek = endOfWeek(subWeeks(now, 1), options);

  return sessions.reduce((acc, item) => {
    const createdAt = new Date(item?.created_at);
    if (!isValid(createdAt)) return acc;

    if (isToday(createdAt)) {
      (acc.today ??= []).push(item);
    } else if (isThisWeek(createdAt, options)) {
      (acc.this_week ??= []).push(item);
    } else if (
      isWithinInterval(createdAt, {
        start: startOfLastWeek,
        end: endOfLastWeek,
      })
    ) {
      (acc.last_week ??= []).push(item);
    } else {
      (acc.older ??= []).push(item);
    }

    return acc;
  }, {});
};

export default function SessionNav() {
  const router = useRouter();
  const { open } = useSidebar();
  const hasToken = hasMaaranToken();
  const {isLoading: isFetchingToken, data:tokenData} = useGenerateMaaranToken(!hasToken);
  const { data, isLoading, error, refetch } = useSessions({ isActive: true });
  const isTourCompleted = useSelector(state => state.tour.completed)

  const { isPending, startNewConversation} = useAssistance();
  const dispatch = useDispatch();

  // Get current session ID from URL
  const sessions = prepareData(data?.items) || {};

  const handleNewChat = () => {
    if (isPending) {
      toast("Please wait for the current response to finish.");
      return;
    }
    dispatch(setPreviewOpen(false));
    startNewConversation();
  };


  



  if(!isTourCompleted) {

    if(router.pathname == "/ai-mode") {
      return null;
    } else {
     return (
       <div className="flex items-center justify-between gap-4 px-4 py-2 hover:bg-gray-200   hover:text-accent-foreground  dark:hover:bg-accent  box-border transition-all duration-300 rounded-xl cursor-pointer my-6"
       onClick={() => router.push('/ai-mode')}
       >
        <Play style={{
          width: 17,
          height: 17,
        }}/>
        <p className="text-[1.4rem]  truncate w-full" >Continue your tour...</p>
      </div>
     )
    }
  };


  // Don't show session list if no token
  if(!hasToken && isFetchingToken) {
    return <div className="h-full flex justify-center items-center">
      <Loader className="spinner-mini" style={{
        width: 20,
        height: 20,
      }} />
    </div>
  }

  if(!hasToken && !tokenData && !isFetchingToken) {
    return <div>
      <p className="text-[1.4rem] p-4 text-foreground/70"> Oops! Something went wrong...  </p>
    </div>
  }

  return (
    <Collapsible defaultOpen className="group/collapsible">
      <SidebarGroup>
        <div
          className="flex items-center gap-4 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-xl transition-colors px-4 py-4"
          onClick={handleNewChat}
          title="Create New Chat"
        >
          <SquarePen className="" />
          {open && <p className="text-2xl ">New Chat</p>}
        </div>
        <SidebarGroupLabel className="my-4 px-4">
          <CollapsibleTrigger className="flex items-center justify-between w-full">
            <span className="text-sidebar-foreground/50 text-[1.3rem]">
              Recent
            </span>
            <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180 size-8 text-sidebar-foreground/50 " />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            {open && (
              <SidebarMenu>
                {isLoading && <Loader2 className="spinner-mini size-8 ml-4" />}

                {/* Error State */}
                {error && (
                  <div className="text-center py-4 text-sidebar-foreground text-[1.2rem]">
                    <p className="mb-4">Failed to load...</p>
                    <button
                      onClick={() => refetch()}
                      className="mt-1 hover:bg-gray-100 py-4 px-4 rounded-xl text-[1.2rem]"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && data?.items?.length === 0 && (
                  <div className="text-center py-4 text-gray-400 text-[1.2rem] px-2">
                    <MessageSquare
                      size={20}
                      className="mx-auto mb-2 opacity-50"
                    />
                    <p>No conversations</p>
                  </div>
                )}

                {/* Session List */}
                {!isLoading &&
                  !error &&
                  ["today", "this_week", "last_week", "older"].map((item) =>
                    sessions[item]?.length > 0 ? (
                      <div key={item}>
                        <span className="ml-3.5 my-3 inline-block capitalize font-semibold text-sidebar-foreground/50 text-[1.3rem]">
                          {item.replace("_", " ")}
                        </span>
                        {sessions[item]?.map((session) => (
                          <SessionItem key={session.id} session={session} />
                        ))}
                      </div>
                    ) : null,
                  )}
              </SidebarMenu>
            )}
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

